import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { AlertTriangle, Package, Phone, Shield as ShieldIcon, FileQuestion, Clock, Truck, Building2, HelpCircle, X } from 'lucide-react';
import API from '@/lib/api';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

const SUB_ESTADO_META = {
  esperando_repuestos: { label: 'Repuestos', icon: Package, color: 'amber' },
  esperando_autorizacion: { label: 'Autorización', icon: ShieldIcon, color: 'orange' },
  esperando_cliente: { label: 'Cliente', icon: Phone, color: 'blue' },
  esperando_pago: { label: 'Pago', icon: Clock, color: 'violet' },
  en_consulta_tecnica: { label: 'Consulta téc.', icon: FileQuestion, color: 'cyan' },
  pendiente_recogida: { label: 'Recogida', icon: Truck, color: 'teal' },
  aseguradora: { label: 'Aseguradora', icon: Building2, color: 'purple' },
  otro: { label: 'Otro', icon: HelpCircle, color: 'gray' },
};

const COLOR_CLASSES = {
  amber: { bg: 'bg-amber-100', text: 'text-amber-800', border: 'border-amber-300', hover: 'hover:bg-amber-200' },
  orange: { bg: 'bg-orange-100', text: 'text-orange-800', border: 'border-orange-300', hover: 'hover:bg-orange-200' },
  blue: { bg: 'bg-blue-100', text: 'text-blue-800', border: 'border-blue-300', hover: 'hover:bg-blue-200' },
  violet: { bg: 'bg-violet-100', text: 'text-violet-800', border: 'border-violet-300', hover: 'hover:bg-violet-200' },
  cyan: { bg: 'bg-cyan-100', text: 'text-cyan-800', border: 'border-cyan-300', hover: 'hover:bg-cyan-200' },
  teal: { bg: 'bg-teal-100', text: 'text-teal-800', border: 'border-teal-300', hover: 'hover:bg-teal-200' },
  purple: { bg: 'bg-purple-100', text: 'text-purple-800', border: 'border-purple-300', hover: 'hover:bg-purple-200' },
  gray: { bg: 'bg-gray-100', text: 'text-gray-800', border: 'border-gray-300', hover: 'hover:bg-gray-200' },
};

const POLL_MS = 60_000;
const HIDDEN_PATHS = ['/login', '/consulta', '/companias'];

/**
 * Strip persistente con los subestados activos del técnico.
 * - Visible solo para admins/master.
 * - Polling cada 60s a /api/alertas/sub-estados.
 * - Cada pill muestra subestado · contador · indicador rojo si hay caducados.
 * - Click navega a /alertas filtrado por ese subestado.
 */
export default function SubestadosStrip({ visible = true }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [alertas, setAlertas] = useState([]);
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem('subestados_strip_collapsed') === '1'; } catch { return false; }
  });

  useEffect(() => {
    if (!visible) return undefined;
    let cancelled = false;
    const fetchOnce = async () => {
      if (typeof document !== 'undefined' && document.hidden) return;
      try {
        const res = await API.get('/alertas/sub-estados');
        if (cancelled) return;
        setAlertas(res?.data?.alertas || []);
      } catch {
        /* ignore */
      }
    };
    fetchOnce();
    const id = setInterval(fetchOnce, POLL_MS);
    return () => { cancelled = true; clearInterval(id); };
  }, [visible]);

  const grupos = useMemo(() => {
    const acc = {};
    alertas.forEach(a => {
      const k = a.subestado || 'otro';
      if (!acc[k]) acc[k] = { total: 0, caducados: 0 };
      acc[k].total += 1;
      if (a.caducado) acc[k].caducados += 1;
    });
    return Object.entries(acc)
      .sort((a, b) => b[1].caducados - a[1].caducados || b[1].total - a[1].total);
  }, [alertas]);

  const totalCaducados = useMemo(
    () => alertas.filter(a => a.caducado).length,
    [alertas],
  );

  const handleToggle = () => {
    const v = !collapsed;
    setCollapsed(v);
    try { localStorage.setItem('subestados_strip_collapsed', v ? '1' : '0'); } catch { /* noop */ }
  };

  // No renderizar en páginas públicas/login
  const isHidden = HIDDEN_PATHS.some(p => location.pathname === p || location.pathname.startsWith(p + '/'));
  if (!visible || isHidden) return null;
  if (grupos.length === 0) return null;

  return (
    <div
      className="mb-4 flex items-center gap-2 flex-wrap rounded-lg border bg-card px-3 py-2 shadow-sm"
      data-testid="subestados-strip"
    >
      <div className="flex items-center gap-2 mr-1">
        <AlertTriangle className={`w-4 h-4 ${totalCaducados > 0 ? 'text-red-600' : 'text-amber-600'}`} />
        <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          Sub-estados técnico
        </span>
        {totalCaducados > 0 && (
          <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-red-600 text-white animate-pulse">
            {totalCaducados} caducado{totalCaducados === 1 ? '' : 's'}
          </span>
        )}
      </div>

      {!collapsed && grupos.map(([key, { total, caducados }]) => {
        const meta = SUB_ESTADO_META[key] || { label: key, icon: HelpCircle, color: 'gray' };
        const Icon = meta.icon;
        const useRed = caducados > 0;
        const colorKey = useRed ? 'orange' : meta.color;
        const c = COLOR_CLASSES[colorKey] || COLOR_CLASSES.gray;
        return (
          <Tooltip key={key}>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={() => navigate(`/alertas?subestado=${encodeURIComponent(key)}`)}
                className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md border ${c.bg} ${c.text} ${c.border} ${c.hover} text-xs font-medium transition-colors ${useRed ? 'ring-1 ring-red-400' : ''}`}
                data-testid={`subestado-pill-${key}`}
              >
                <Icon className="w-3.5 h-3.5" />
                <span>{meta.label}</span>
                <span className="font-bold tabular-nums">{total}</span>
                {caducados > 0 && (
                  <span className="ml-0.5 px-1 rounded bg-red-600 text-white text-[10px] font-bold tabular-nums">
                    {caducados}
                  </span>
                )}
              </button>
            </TooltipTrigger>
            <TooltipContent>
              {meta.label}: {total} OT{total === 1 ? '' : 's'}
              {caducados > 0 ? ` · ${caducados} caducada${caducados === 1 ? '' : 's'}` : ''}
            </TooltipContent>
          </Tooltip>
        );
      })}

      {collapsed && (
        <span className="text-xs text-muted-foreground">
          {grupos.length} tipo{grupos.length === 1 ? '' : 's'} · {alertas.length} OTs
        </span>
      )}

      <button
        type="button"
        onClick={handleToggle}
        className="ml-auto text-muted-foreground hover:text-foreground transition-colors p-1 rounded hover:bg-muted"
        title={collapsed ? 'Mostrar detalle' : 'Compactar'}
        data-testid="subestados-strip-toggle"
      >
        <X className={`w-3.5 h-3.5 transition-transform ${collapsed ? 'rotate-45' : ''}`} />
      </button>
    </div>
  );
}
